import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { clearSocketBuffer, connectSocket, socket } from '../socket';


function getRoomCode(payload) {
  return payload?.roomCode || payload?.room?.code || payload?.code || '';
}

export const ROOM_ROUTE_BASE = '/rooms';

export function buildRoomRoute(roomCode = '') {
  return `${ROOM_ROUTE_BASE}/${roomCode}`;
}

export function normalizeJoinRoomCodeInput(value) {
  return String(value || '').replace(/\D/g, '').slice(0, 4);
}

export function buildBuyInSeatOptions({ maxPlayers = 10, players = [], selectedSeatIndex = 0 } = {}) {
  const occupiedSeats = new Map(
    players
      .filter((player) => Number.isInteger(Number(player.seatIndex)))
      .map((player) => [Number(player.seatIndex), player])
  );
  return Array.from({ length: maxPlayers }, (_, seatIndex) => {
    const occupant = occupiedSeats.get(seatIndex) || null;
    const occupied = Boolean(occupant);
    return {
      seatIndex,
      occupied,
      disabled: occupied,
      active: !occupied && selectedSeatIndex === seatIndex,
      occupantName: occupant?.displayName || ''
    };
  });
}

export function firstSelectableSeatIndex({ maxPlayers = 10, players = [], selectedSeatIndex = 0 } = {}) {
  const seatOptions = buildBuyInSeatOptions({ maxPlayers, players, selectedSeatIndex });
  const selectedSeat = seatOptions.find((seat) => seat.seatIndex === selectedSeatIndex);
  if (selectedSeat && !selectedSeat.disabled) {
    return selectedSeatIndex;
  }
  const firstOpenSeat = seatOptions.find((seat) => !seat.disabled);
  return firstOpenSeat ? firstOpenSeat.seatIndex : -1;
}

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [roomCode, setRoomCode] = useState('');
  const [error, setError] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const socketActionCleanupRef = useRef(null);


  useEffect(() => () => {
    if (socketActionCleanupRef.current) {
      socketActionCleanupRef.current();
    }
  }, []);

  const runRoomAction = ({
    action,
    successEvent,
    emitEvent,
    emitPayload,
    failureMessage,
    onSuccess
  }) => {
    if (socketActionCleanupRef.current) {
      socketActionCleanupRef.current();
    }

    setError('');
    setBusyAction(action);

    let settled = false;

    const cleanup = () => {
      socket.off('connect', handleConnect);
      socket.off(successEvent, handleSuccess);
      socket.off('error', handleSocketError);
      socket.off('connect_error', handleConnectError);
      if (socketActionCleanupRef.current === cleanup) {
        socketActionCleanupRef.current = null;
      }
    };

    const finish = (callback) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      setBusyAction('');
      callback();
    };

    function handleSuccess(payload) {
      finish(() => onSuccess(payload));
    }

    function handleConnect() {
      socket.emit(emitEvent, emitPayload);
    }

    function handleSocketError(payload = {}) {
      clearSocketBuffer();
      finish(() => setError(payload.message || failureMessage));
    }

    function handleConnectError(errorPayload = {}) {
      clearSocketBuffer();
      finish(() => setError(errorPayload.message || failureMessage));
    }

    socketActionCleanupRef.current = cleanup;
    socket.once(successEvent, handleSuccess);
    socket.once('error', handleSocketError);
    socket.once('connect_error', handleConnectError);
    if (socket.connected) {
      handleConnect();
    } else {
      socket.once('connect', handleConnect);
      connectSocket();
    }
  };

  const handleCreateRoom = () => {
    setError('');
    runRoomAction({
      action: 'create',
      successEvent: 'roomCreated',
      emitEvent: 'createRoom',
      emitPayload: {},
      failureMessage: '无法创建房间',
      onSuccess: (payload) => {
        const roomPayload = payload?.room || payload;
        const updatedCode = getRoomCode(payload);
        navigate(buildRoomRoute(updatedCode), {
          state: { room: roomPayload }
        });
      }
    });
  };

  const handleJoinRoom = (event) => {
    event.preventDefault();
    const normalizedCode = normalizeJoinRoomCodeInput(roomCode);
    if (!normalizedCode) {
      setError('请输入房间码后再加入。');
      return;
    }
    navigate(buildRoomRoute(normalizedCode));
  };

  return (
    <main className="page-shell page-shell--themed texas-lobby-page">
      <header className="page-hero brand-hero brand-hero--home texas-hero">
        <div>
          <h1>德州扑克大厅</h1>
        </div>
      </header>

      {error ? <div className="error-message">{error}</div> : null}

      <section className="data-card home-actions texas-lobby-card">
        <button className="texas-primary-button" type="button" onClick={handleCreateRoom} disabled={Boolean(busyAction)}>
          {busyAction === 'create' ? '创建中...' : '创建房间'}
        </button>

        <form className="texas-join-form" onSubmit={handleJoinRoom}>
          <label className="field-label" htmlFor="texas-room-code">
            房间码
          </label>
          <div className="texas-join-row">
            <input
              id="texas-room-code"
              aria-label="房间码"
              inputMode="numeric"
              maxLength="4"
              pattern="[0-9]{4}"
              placeholder="输入 4 位房间码"
              value={roomCode}
              onChange={(event) => setRoomCode(normalizeJoinRoomCodeInput(event.target.value))}
            />
            <button className="texas-secondary-button" type="submit" disabled={Boolean(busyAction)}>
              {busyAction === 'preview' ? '读取中...' : '加入房间'}
            </button>
          </div>
        </form>
      </section>

      <nav className="home-links" aria-label="游戏导航">
        <Link className="pill-link" to="/">返回游戏库</Link>
        <Link className="pill-link" to="/stats">统计</Link>
        {user.role === 'admin' && <Link className="pill-link" to="/admin/texas-holdem">管理</Link>}
      </nav>
    </main>
  );
};

export default HomePage;
